import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, MapPin, ArrowClockwise, Check, ShieldCheck, Warning } from "@phosphor-icons/react";

export default function StaffPunch() {
  const { user } = useAuth();
  const [type, setType] = useState("IN");
  const [duty, setDuty] = useState("OFFICE");
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [coords, setCoords] = useState(null);
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [verify, setVerify] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "user" }, width: 720, height: 720 }, audio: false,
        });
        if (!active) { s.getTracks().forEach((t) => t.stop()); return; }
        setStream(s);
        if (videoRef.current) { videoRef.current.srcObject = s; }
      } catch (e) {
        toast.error("Camera permission denied. Enable camera to punch.");
      }
    })();
    (async () => {
      if (!navigator.geolocation) { toast.error("GPS not supported on this device"); return; }
      navigator.geolocation.getCurrentPosition(
        (p) => setCoords({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
        () => toast.error("Enable location to capture GPS"),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    })();
    return () => { active = false; stream?.getTracks?.().forEach((t) => t.stop()); };
    // eslint-disable-next-line
  }, []);

  // Load assigned locations once user available
  useEffect(() => {
    if (!user?.id) return;
    api.get(`/employees/${user.id}/locations`)
       .then((r) => {
         setLocations(r.data);
         if (r.data.length === 1) setLocationId(r.data[0].id);
       })
       .catch(() => {});
  }, [user?.id]);

  // Live verify when coords or location change
  useEffect(() => {
    if (!coords || !locationId) { setVerify(null); return; }
    api.post("/locations/verify", { latitude: coords.latitude, longitude: coords.longitude, location_id: locationId })
       .then((r) => setVerify(r.data))
       .catch(() => setVerify(null));
  }, [coords, locationId]);

  function capture() {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const W = 720, H = 720;
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    // draw video square
    const min = Math.min(v.videoWidth, v.videoHeight);
    const sx = (v.videoWidth - min) / 2;
    const sy = (v.videoHeight - min) / 2;
    ctx.drawImage(v, sx, sy, min, min, 0, 0, W, H);
    // watermark
    const now = new Date();
    const lines = [
      `${user.name}  ·  ${user.employee_id}`,
      `${now.toLocaleString()}`,
      `Lat ${coords?.latitude?.toFixed(5) || "-"}  Lon ${coords?.longitude?.toFixed(5) || "-"}`,
      verify?.location_name
        ? `${verify.location_name} (${verify.location_code})  ·  ${Math.round(verify.distance_m)}m  ·  ${verify.status}`
        : `${type}  ·  ${duty} DUTY  ·  ${user.shift}`,
    ];
    // gradient bar at bottom
    const grad = ctx.createLinearGradient(0, H - 180, 0, H);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.85)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, H - 180, W, 180);
    // red accent
    ctx.fillStyle = "#E50914";
    ctx.fillRect(20, H - 165, 4, 100);
    ctx.font = "bold 22px IBM Plex Sans, sans-serif";
    ctx.fillStyle = "#fff";
    let y = H - 130;
    for (const line of lines) {
      ctx.fillText(line, 36, y);
      y += 28;
    }
    ctx.font = "bold 13px IBM Plex Sans, sans-serif";
    ctx.fillStyle = "#E50914";
    ctx.fillText("RESCIGO HEALTH · WMS", 36, H - 20);
    const data = c.toDataURL("image/jpeg", 0.78);
    setPhoto(data);
  }

  function retake() { setPhoto(null); }

  async function submit() {
    if (!photo) { toast.error("Capture a selfie first"); return; }
    if (!coords) { toast.error("GPS coordinates missing"); return; }
    setBusy(true);
    try {
      const body = {
        type, duty, photo,
        latitude: coords.latitude, longitude: coords.longitude,
        accuracy: coords.accuracy || 0,
        location_name: verify?.location_name || "",
        device_info: navigator.userAgent.slice(0, 80),
        distance_m: verify?.distance_m || 0,
        location_id: locationId || null,
      };
      await api.post("/attendance/punch", body);
      toast.success(`Punch ${type} recorded`);
      setPhoto(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="px-5 pt-5 pb-12 max-w-md mx-auto">
      <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-1">Live Capture</p>
      <h1 className="font-display text-3xl tracking-tight">Punch Attendance</h1>

      <div className="grid grid-cols-2 gap-3 mt-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Type</div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="bg-[#16161C] border-[#2D2D35]" data-testid="punch-type-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
              <SelectItem value="IN">IN</SelectItem>
              <SelectItem value="OUT">OUT</SelectItem>
              <SelectItem value="BREAK_START">Break Start</SelectItem>
              <SelectItem value="BREAK_END">Break End</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Duty</div>
          <Select value={duty} onValueChange={setDuty}>
            <SelectTrigger className="bg-[#16161C] border-[#2D2D35]" data-testid="duty-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
              <SelectItem value="OFFICE">Office</SelectItem>
              <SelectItem value="FIELD">Field</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Location selector */}
      {locations.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Reporting Location</div>
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="bg-[#16161C] border-[#2D2D35]" data-testid="punch-location-select">
              <SelectValue placeholder="Select assigned location" />
            </SelectTrigger>
            <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
              {locations.map((l) => (
                <SelectItem key={l.id} value={l.id}>{l.name} · {l.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {verify && (
            <div
              data-testid="punch-verify-badge"
              className={`mt-2 flex items-center justify-between px-3 py-2 rounded text-xs ${
                verify.status === "VALID" ? "bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30" :
                verify.status === "LOCATION_MISMATCH" ? "bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30" :
                "bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30"
              }`}>
              <span className="inline-flex items-center gap-1.5">
                {verify.status === "VALID" ? <ShieldCheck size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
                {verify.status.replace("_", " ")}
              </span>
              <span className="font-mono">{Math.round(verify.distance_m)} m / {verify.allowed_radius} m</span>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 aspect-square bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden relative">
        {photo ? (
          <img src={photo} alt="preview" className="w-full h-full object-cover" data-testid="punch-preview" />
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="punch-video" />
        )}
        <canvas ref={canvasRef} className="hidden" />
        {coords && (
          <div className="absolute top-3 left-3 text-[10px] font-mono px-2 py-1 bg-black/60 text-white rounded inline-flex items-center gap-1">
            <MapPin size={11} className="text-[#E50914]" />
            {coords.latitude.toFixed(4)},{coords.longitude.toFixed(4)}
          </div>
        )}
        {!coords && (
          <div className="absolute top-3 left-3 text-[10px] px-2 py-1 bg-[#FF3B30]/30 text-white rounded">Waiting for GPS…</div>
        )}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        {!photo ? (
          <Button onClick={capture} disabled={!stream}
                  className="col-span-2 h-14 bg-[#E50914] hover:bg-[#C80812] text-base"
                  data-testid="capture-button">
            <Camera size={20} className="mr-2" weight="bold" /> Capture
          </Button>
        ) : (
          <>
            <Button onClick={retake} variant="outline" className="h-12 border-[#2D2D35] bg-transparent" data-testid="retake-button">
              <ArrowClockwise size={18} className="mr-1" /> Retake
            </Button>
            <Button onClick={submit} disabled={busy} className="h-12 bg-[#E50914] hover:bg-[#C80812]" data-testid="submit-punch-button">
              <Check size={18} className="mr-1" weight="bold" /> {busy ? "Sending…" : `Submit ${type}`}
            </Button>
          </>
        )}
      </div>

      <p className="text-[10px] text-zinc-500 text-center mt-4 leading-relaxed">
        Gallery uploads are not permitted. Photo is watermarked with your name, ID, GPS and time.
      </p>
    </div>
  );
}
